import { cachedRequest } from './requestCache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const handleApiError = (error: any): Error => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
};

// --- Type Definitions ---

export interface LeaderboardEntry {
  salesperson_id: string;
  salesperson_name: string;
  total_conversations: number;
  avg_buyer_interest_score: number;
  high_intent_rate: number;
  avg_sop_score: number;
  high_sop_rate: number;
  avg_duration: number | null;  // Average meeting duration in minutes
  last_activity_at: string;
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  total_salespersons: number;
  time_range: string;
}

// Raw API shape (backend returns `rows` and `total_salespeople`)
interface LeaderboardApiResponse {
  rows: LeaderboardEntry[];
  total_salespeople: number;
  time_range: string;
  business_identifier?: string | null;
  generated_at?: string;
}

export interface SalespersonMetrics {
  salesperson_id: string;
  total_conversations: number;
  sop_adherence_percentage: number;
  weighted_stage_score: number;
  objection_handling_score: number;
  average_talk_percentage: number;
  average_listen_percentage: number;
  dominant_speaker_ratio: string;
  trend: {
    sop_adherence: number;
    objection_handling: number;
    talk_listen_balance: number;
  };
}

export interface DealSentimentBatch {
  conversation_id: string;
  overall_deal_sentiment_score: number | null;
  buyer_interest_level_score: number | null;
  overall_deal_sentiment_text: string | null;
}

export interface TopObjectionCategory {
  category: string;
  count: number;
  percentage: number;
  objections: Array<{ objection_text: string; count: number }>;
}

export interface TopObjectionsSummaryResponse {
  business_identifier?: string;
  categories: TopObjectionCategory[];
}

export interface SentimentTrendPoint {
  period: string;
  avg_score: number;
  conversation_count: number;
}

export interface SentimentTrendResponse {
  business_identifier?: string;
  salesperson_id?: string;
  time_range: string;
  group_by: string;
  data: SentimentTrendPoint[];
}

export interface ObjectionsTrendPoint {
  period: string;
  category: string;
  count: number;
}

export interface ObjectionsTrendResponse {
  business_identifier: string;
  time_range: string;
  group_by: string;
  top_categories: string[];
  data: ObjectionsTrendPoint[];
}

export interface UnitInterestEntry {
  unit_type: string;
  count: number;
  percentage: number;
}

export interface UnitInterestResponse {
  business_identifier: string;
  total_conversations: number;
  unit_types: UnitInterestEntry[];
}

export interface RiskEntry {
  category: string;
  count: number;
  percentage: number;
}

export interface RiskBreakdownResponse {
  business_identifier: string;
  total_conversations: number;
  risks: RiskEntry[];
}

export interface DealSizeTrendPoint {
  period: string;
  avg_deal_value: number;
  count: number;
}

export interface BudgetBucket {
  range: string;
  count: number;
  percentage: number;
}

export interface EmotionalUpliftEntry {
  conversation_id: string;
  start_score: number;
  end_score: number;
  delta: number;
  trajectory: string;
}

export interface AmenityMentionEntry {
  amenity: string;
  count: number;
  positive: number;
  negative: number;
  neutral: number;
}

export interface IntentDistEntry {
  intent_type: string;
  count: number;
  percentage: number;
}

export interface LuxuryBucket {
  range: string;
  count: number;
}

// --- Service Class ---

class AnalyticsService {
  private async makeRequest<T>(url: string, options?: RequestInit): Promise<T> {
    const method = (options?.method || 'GET').toUpperCase();
    // Only GET responses are safe to cache/de-duplicate.
    if (method === 'GET') {
      const key = `analytics GET ${url}`;
      return cachedRequest(key, () => this.doRequest<T>(url, options));
    }
    return this.doRequest<T>(url, options);
  }

  private async doRequest<T>(url: string, options?: RequestInit): Promise<T> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-API-Key': 'h-HdEURZOkbVll1-skwSiK9m1kAaRdXIpss5wl7d0EM',
      };

      if (options?.headers) {
        Object.assign(headers, options.headers);
      }

      const response = await fetch(url, {
        method: 'GET',
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AnalyticsService] HTTP error! status: ${response.status}, response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`[AnalyticsService] Request failed for ${url}:`, error);
      throw handleApiError(error);
    }
  }

  /**
   * Get the sales leaderboard with optional filters.
   */
  async getSalesLeaderboard(
    businessIdentifier?: string,
    timeRange: string = '7d',
    sortBy: string = 'avg_interest',
    limit: number = 100
  ): Promise<LeaderboardResponse> {
    const params = new URLSearchParams();
    if (businessIdentifier) params.append('business_identifier', businessIdentifier);
    params.append('time_range', timeRange);
    params.append('sort_by', sortBy);
    params.append('limit', String(limit));

    const raw = await this.makeRequest<LeaderboardApiResponse>(
      `${API_BASE_URL}/aggregations/sales_leaderboard?${params.toString()}`
    );

    // Normalize: backend returns `rows` / `total_salespeople`,
    // but our frontend types use `leaderboard` / `total_salespersons`
    return {
      leaderboard: raw.rows || [],
      total_salespersons: raw.total_salespeople ?? 0,
      time_range: raw.time_range,
    };
  }

  /**
   * Get detailed metrics for a single salesperson.
   */
  async getSalespersonMetrics(salespersonId: string): Promise<SalespersonMetrics> {
    return this.makeRequest<SalespersonMetrics>(
      `${API_BASE_URL}/aggregations/salesperson/${salespersonId}/metrics_summary`
    );
  }

  /**
   * Get deal sentiments for a batch of conversation IDs.
   */
  async getBatchDealSentiments(conversationIds: string[]): Promise<DealSentimentBatch[]> {
    return this.makeRequest<DealSentimentBatch[]>(
      `${API_BASE_URL}/aggregations/conversations/deal_sentiments`,
      {
        method: 'POST',
        body: JSON.stringify({ conversation_ids: conversationIds }),
      }
    );
  }

  /**
   * Get top objections summary for a business or overall.
   */
  async getTopObjectionsSummary(businessIdentifier?: string): Promise<TopObjectionsSummaryResponse> {
    const endpoint = businessIdentifier
      ? `/aggregations/business/${businessIdentifier}/top_objections_summary`
      : '/aggregations/overall/top_objections_summary';

    return this.makeRequest<TopObjectionsSummaryResponse>(`${API_BASE_URL}${endpoint}`);
  }
  /**
   * Get sentiment trend for a business or overall.
   */
  async getBusinessSentimentTrend(businessIdentifier?: string, timeRange: string = '90d', groupBy: string = 'month'): Promise<SentimentTrendResponse> {
    const biz = businessIdentifier ? `business/${businessIdentifier}` : 'overall';
    return this.makeRequest(`${API_BASE_URL}/aggregations/${biz}/sentiment_trend?time_range=${timeRange}&group_by=${groupBy}`);
  }

  /**
   * Get sentiment trend for a specific salesperson.
   */
  async getSalespersonSentimentTrend(salespersonId: string, timeRange: string = '90d', groupBy: string = 'month'): Promise<SentimentTrendResponse> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/salesperson/${salespersonId}/sentiment_trend?time_range=${timeRange}&group_by=${groupBy}`);
  }

  /**
   * Get objections trend over time.
   */
  async getObjectionsTrend(businessIdentifier?: string, timeRange: string = '90d', groupBy: string = 'week', topN: number = 5): Promise<ObjectionsTrendResponse> {
    const biz = businessIdentifier || '';
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${biz}/objections_trend?time_range=${timeRange}&group_by=${groupBy}&top_n=${topN}`);
  }

  /**
   * Get unit interest distribution for a business.
   */
  async getUnitInterestDistribution(businessIdentifier: string): Promise<UnitInterestResponse> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${businessIdentifier}/unit_interest_distribution`);
  }

  /**
   * Get risk breakdown for a business.
   */
  async getRiskBreakdown(businessIdentifier: string): Promise<RiskBreakdownResponse> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${businessIdentifier}/risk_breakdown`);
  }
  /**
   * Get deal size trend for a business.
   */
  async getDealSizeTrend(bizId: string, timeRange: string = '90d', groupBy: string = 'month'): Promise<{ data: DealSizeTrendPoint[] }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/deal_size_trend?time_range=${timeRange}&group_by=${groupBy}`);
  }

  /**
   * Get budget distribution for a business.
   */
  async getBudgetDistribution(bizId: string): Promise<{ data: BudgetBucket[] }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/budget_distribution`);
  }

  /**
   * Get emotional uplift data for a business.
   */
  async getEmotionalUplift(bizId: string): Promise<{ data: EmotionalUpliftEntry[] }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/emotional_uplift`);
  }

  /**
   * Get amenity mentions for a business.
   */
  async getAmenityMentions(bizId: string): Promise<{ data: AmenityMentionEntry[] }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/amenity_mentions`);
  }

  /**
   * Get buyer intent distribution for a business.
   */
  async getBuyerIntentDistribution(bizId: string): Promise<{ data: IntentDistEntry[] }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/buyer_intent_distribution`);
  }

  /**
   * Get luxury intent distribution for a business.
   */
  async getLuxuryIntentDistribution(bizId: string): Promise<{ data: LuxuryBucket[] }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/luxury_intent_distribution`);
  }

  /**
   * Get client names for a specific unit type (drill-down).
   */
  async getUnitTypeClients(bizId: string, unitType: string): Promise<{ unit_type: string; total_clients: number; clients: Array<{ conversation_id: string; client_name: string }> }> {
    return this.makeRequest(`${API_BASE_URL}/aggregations/business/${bizId}/unit_type_clients?unit_type=${encodeURIComponent(unitType)}`);
  }
}

export const analyticsService = new AnalyticsService();
